import React, { useEffect, useState } from "react"
import PubSub from "pubsub-js"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { BanknotesIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import { navigate } from "vike/client/router"
import useT from "../hooks/useT"
import Spinner from "../ui/Spinner"
import { clearUTM } from "./utm"
import { hasPhysicalItems } from "./cartItems"
import { PURCHASE_STATE_TOPIC, purchaseState, settlePurchase } from "./purchase"

/**
 * Order placed (fragment "payment-success", rendered by the framework
 * payment-success element once the receipt is set).
 *
 * Two screens, not one: while the webhook is still creating the order the
 * buyer is asked to wait, and only a settled purchase is thanked for. The
 * waiting itself belongs to `settlePurchase` (checkout/purchase.js), which
 * carries on whether this dialog is open or not — a buyer who closes it early
 * still ends up owning what they bought.
 *
 * `children` is what the framework offers to append — the product's feature
 * buttons. A shop sells more than one thing, so the end of an order is no
 * place for one product's features: they are left out here.
 */
export default function SuccessStep({ store, onClose }) {
  const t = useT()
  const receipt = store.paymentReceipt
  // placed to be paid on delivery: the orders exist already, no payment to wait for
  const cashOnDelivery = !!receipt?.cashOnDelivery
  const paymentIntentId = cashOnDelivery ? null : receipt?.paymentIntentId || receipt?.paymentInfo?.paymentIntentId
  const email = receipt?.email
  const lines = receipt?.cart || []
  const digital = !hasPhysicalItems(lines)
  // "validating" until the order exists, then "done" — or "failed" when the
  // wait gave up, which is not the same as the order being lost
  const [phase, setPhase] = useState(() => (paymentIntentId ? purchaseState(paymentIntentId) || "validating" : "done"))

  useEffect(() => {
    clearUTM()
    if (!paymentIntentId) return
    const token = PubSub.subscribe(PURCHASE_STATE_TOPIC, (_, data) => {
      if (data?.paymentIntentId === paymentIntentId) setPhase(data.state)
    })
    // started here or already running from an earlier mount: either way this
    // screen only watches it
    settlePurchase(paymentIntentId, { pids: lines.map((line) => line.pid).filter(Boolean) })
    setPhase(purchaseState(paymentIntentId) || "validating")
    return () => PubSub.unsubscribe(token)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentIntentId])

  const close = () => (onClose ? onClose() : store.setPaymentSuccessVisible(false))

  // the bank has the money; the shop is still writing the order down
  if (phase === "validating") {
    return (
      <div className="flex flex-col items-center justify-center gap-6 py-10 px-4 text-center min-h-[calc(100dvh-160px)] md:min-h-0" data-testid="checkout-validating">
        <Spinner />
        <h3 className="m-0 text-[22px] font-bold">{t`Please wait while we validate your purchase`}</h3>
        <p className="m-0 max-w-[460px] text-[15px] text-text_secondary">
          {t`This takes a few seconds. Your payment has gone through — we are putting the order together.`}
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center gap-6 py-6 px-4 text-center min-h-[calc(100dvh-160px)] md:min-h-0" data-testid="checkout-success">
      <CheckCircleIcon className="w-[96px] h-[96px] text-success" />
      <h3 className="m-0 text-[28px] font-bold">{t`Thank you for your order!`}</h3>
      {/* to be paid on delivery: said first, the amount being the courier's to collect */}
      {cashOnDelivery && (
        <p className="m-0 max-w-[460px] inline-flex items-center gap-2 rounded-lg bg-warning/10 px-3 py-2 text-[14px] font-semibold text-text" data-testid="success-cash-on-delivery">
          <BanknotesIcon className="w-5 h-5 shrink-0 text-warning" />
          {receipt?.atShop ? t`You pay at the shop when you collect the order` : t`You pay the courier when the parcel arrives`}
        </p>
      )}
      <p className="m-0 max-w-[460px] text-[15px]">
        {t`Your order has been successfully placed.`}{" "}
        {digital ? t`Your products are available in your profile.` : t`You will receive a confirmation email shortly at`}
        {!digital && email && (
          <>
            {" "}
            <strong>{email}</strong>
          </>
        )}
      </p>
      {phase === "failed" && (
        <p className="m-0 max-w-[460px] text-[13px] text-text_secondary">
          {t`Your order is taking longer than usual to appear. It is paid for and on its way — you will find it under your orders.`}
        </p>
      )}
      {/* stacked, the width of the dialog's other actions — as the sign-in
          dialog stacks its own */}
      <div className="w-full max-w-[360px] flex flex-col gap-3">
        <Button
          variant="primary_l"
          className="w-full"
          data-testid="go-to-orders"
          onClick={() => {
            close()
            navigate("/user/orders")
          }}
        >
          {t`Go to my orders`}
        </Button>
        <Button variant="secondary" className="w-full" onClick={close}>
          {t`Continue shopping`}
        </Button>
      </div>
    </div>
  )
}
